import {
  EmailAlreadyExistsError,
  PersonalEmailNotAllowedError,
} from "@/api/IdentityApi";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { useIdentity } from "@/components/contexts/IdentityContext/UseIdentity";
import { LoadingDots } from "@/components/ui/LoadingDots";
import { Logo } from "@/components/ui/Logo";
import { Heading, Text } from "@/components/ui/Typography";
import { UAEPassButton } from "@/components/ui/UAEPassButton";
import { Button } from "@/components/ui/shadcn/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/shadcn/form";
import { FormInput } from "@/components/ui/shadcn/input";
import { Link } from "@/components/ui/shadcn/link";
import { CountryCodeInput } from "@/components/ui/CountryCodeInput";
import { extractUserTypeFromReturnUrl } from "@/lib/identityUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import parsePhoneNumber from "libphonenumber-js";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { useLocation, useNavigate, useSearchParams } from "react-router";
import { z } from "zod";
import { animDelay } from "@/lib/utils";
import { UserType } from "@/types/IdentityTypes";

const passwordRules = z
  .string()
  .nonempty("Password is required")
  .min(8, "Password must be at least 8 characters long")
  .max(100, "Password must be at most 100 characters long")
  .regex(/[0-9]/, "Password must contain at least one digit")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .refine(
    (password) => new Set(password.split("")).size >= 4,
    "Password must contain at least 4 unique characters",
  );

const signupSchema = z
  .object({
    firstName: z
      .string()
      .nonempty("First name is required")
      .max(100, "First name must be at most 100 characters"),
    lastName: z
      .string()
      .nonempty("Last name is required")
      .max(100, "Last name must be at most 100 characters"),
    email: z
      .string()
      .nonempty("Email is required")
      .email("Invalid email address")
      .max(256, "Email must be at most 256 characters long"),
    phoneCountryCode: z.object({
      countryCode: z.string(),
      dialCode: z.string(),
    }),
    phoneNumber: z.string().nonempty("Phone number is required"),
    password: passwordRules,
  })
  .refine(
    (data) => {
      const phone = parsePhoneNumber(
        `${data.phoneCountryCode.dialCode}${data.phoneNumber}`,
      );
      return phone?.isValid();
    },
    {
      path: ["phoneNumber"],
      message: "Invalid phone number",
    },
  );

type SignupData = z.infer<typeof signupSchema>;

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const isAgent = searchParams.get("isAgent") === "true";
  const { registerAgent, registerUser, externalLogin } = useIdentity();
  const navigate = useNavigate();
  const { state } = useLocation();

  const userTypeFromHint = extractUserTypeFromReturnUrl(state?.from);

  const form = useForm<SignupData>({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phoneCountryCode: { countryCode: "AE", dialCode: "+971" },
      phoneNumber: "",
      password: "",
    },
    resolver: zodResolver(signupSchema),
  });

  const handleUaePassLogin = useCallback(() => {
    const returnUrl = state?.from || window.location.origin;
    externalLogin("UaePass", encodeURIComponent(returnUrl));
  }, [externalLogin, state]);

  const onSubmit = useCallback(
    async (data: SignupData) => {
      form.clearErrors();
      // Submit the canonical E.164 from libphonenumber, not the raw
      // dialCode+input concat. Otherwise inputs in national format
      // (e.g. Egyptian "01003422911") would be persisted with the
      // trunk '0' baked in (+2001003422911) instead of the canonical
      // form (+201003422911), breaking dedupe and downstream messaging.
      const parsed = parsePhoneNumber(
        `${data.phoneCountryCode.dialCode}${data.phoneNumber}`,
      );
      const fullPhone =
        parsed?.number ??
        `${data.phoneCountryCode.dialCode}${data.phoneNumber}`;

      try {
        if (isAgent) {
          await registerAgent({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phoneNumber: fullPhone,
            password: data.password,
          });
        } else {
          const regularUserType =
            userTypeFromHint === UserType.Tenant ||
            userTypeFromHint === UserType.Landlord
              ? userTypeFromHint
              : undefined;
          await registerUser({
            firstName: data.firstName,
            lastName: data.lastName,
            email: data.email,
            phoneNumber: fullPhone,
            password: data.password,
            userType: regularUserType,
          });
        }

        navigate(`/verify-email?email=${data.email}`, {
          state: state,
        });
      } catch (error) {
        if (error instanceof PersonalEmailNotAllowedError) {
          form.setError("email", {
            type: "personalEmail",
            message:
              "Personal email addresses are not allowed for agent accounts.",
          });
        } else if (error instanceof EmailAlreadyExistsError) {
          form.setError("root", {
            message:
              "You already have an account with this email. Try signing in.",
          });
        } else {
          form.setError("root", {
            message: "An error occurred while signing up. Please try again.",
          });
        }
      }
    },
    [
      form,
      isAgent,
      navigate,
      state,
      registerAgent,
      registerUser,
      userTypeFromHint,
    ],
  );

  return (
    <AuthLayout>
      <div className="flex flex-col items-center text-center">
        <header
          className="anim-slide-up flex flex-col items-center gap-3"
          style={animDelay(50)}
        >
          <Logo variant="A" className="mb-4 w-[144px] sm:w-[156px]" />
          <Heading
            variant="h1"
            weight="medium"
            className="text-3xl sm:text-3xl"
          >
            {isAgent ? "Sign up as an agent" : "Create your account"}
          </Heading>
          <p className="text-sm text-muted-foreground sm:text-md">
            {isAgent
              ? "Connect with qualified renters, manage listings, and grow your business."
              : "Get started in under a minute."}
          </p>
        </header>

        <div className="anim-slide-up mt-6 w-full" style={animDelay(150)}>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
              <div className="mx-auto flex w-full flex-col items-center text-left">
                <div className="flex w-full flex-col gap-3.5">
                  {/* First Name / Last Name side by side */}
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      control={form.control}
                      name="firstName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>First Name</FormLabel>
                          <FormControl>
                            <FormInput
                              placeholder="First name"
                              autoComplete="given-name"
                              disabled={form.formState.isSubmitting}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="lastName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Last Name</FormLabel>
                          <FormControl>
                            <FormInput
                              placeholder="Last name"
                              autoComplete="family-name"
                              disabled={form.formState.isSubmitting}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Email */}
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field, fieldState }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <FormInput
                            placeholder="Enter your email"
                            autoComplete="email"
                            disabled={form.formState.isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        {fieldState.error?.type === "personalEmail" ? (
                          <div className="anim-slide-down rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm dark:border-amber-900/50 dark:bg-amber-950/30">
                            <p className="font-medium text-amber-800 dark:text-amber-200">
                              Agent accounts require a corporate email.
                            </p>
                            <p className="mt-1 text-amber-700 dark:text-amber-300/80">
                              Want to use this email?{" "}
                              <button
                                type="button"
                                className="font-semibold underline underline-offset-2"
                                onClick={() => {
                                  form.clearErrors();
                                  navigate("/signup", { state });
                                }}
                              >
                                Sign up as a regular user
                              </button>{" "}
                              instead.
                            </p>
                          </div>
                        ) : (
                          <FormMessage />
                        )}
                      </FormItem>
                    )}
                  />

                  {/* Phone Number */}
                  <FormField
                    control={form.control}
                    name="phoneNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone Number</FormLabel>
                        <div className="flex w-full gap-2">
                          <FormField
                            control={form.control}
                            name="phoneCountryCode"
                            render={({ field: ccField }) => (
                              <CountryCodeInput
                                description="Phone Number Country Code"
                                value={ccField.value}
                                onChange={ccField.onChange}
                                disabled={form.formState.isSubmitting}
                              />
                            )}
                          />
                          <FormControl>
                            <FormInput
                              id="phoneNumber"
                              data-testid="phone-number-input"
                              placeholder="50 123 4567"
                              autoComplete="tel-national"
                              type="tel"
                              disabled={form.formState.isSubmitting}
                              {...field}
                            />
                          </FormControl>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Password */}
                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <FormInput
                            placeholder="Create a password"
                            type="password"
                            autoComplete="new-password"
                            disabled={form.formState.isSubmitting}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div
                  className="anim-slide-up mt-5 w-full"
                  style={animDelay(250)}
                >
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={form.formState.isSubmitting}
                  >
                    {form.formState.isSubmitting ? (
                      <LoadingDots color="current" />
                    ) : (
                      "Create account"
                    )}
                  </Button>
                </div>

                {form.formState.errors.root && (
                  <div className="anim-slide-down mt-3 w-full">
                    <Text
                      variant="text3"
                      className="text-center text-destructive"
                    >
                      {form.formState.errors.root.message}
                    </Text>
                  </div>
                )}
              </div>
            </form>
          </Form>

          {!isAgent && (
            <div className="anim-slide-up" style={animDelay(300)}>
              <div className="my-4 flex items-center gap-3">
                <div className="h-px flex-1 bg-border" />
                <span className="text-xs font-medium tracking-wide uppercase text-muted-foreground">
                  or
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>
              <UAEPassButton
                onClick={handleUaePassLogin}
                text="Sign up with UAE PASS"
              />
            </div>
          )}
        </div>

        <div className="anim-fade-in mt-5" style={animDelay(400)}>
          <Text variant="text3">
            Already have an account?{" "}
            <Link
              to={`/signin${isAgent || userTypeFromHint === UserType.Agent ? "?isAgent=true" : ""}`}
              state={state}
            >
              Sign in
            </Link>
          </Text>
        </div>
      </div>
    </AuthLayout>
  );
}
